import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  Help,
  LocalFireDepartment,
  FitnessCenter,
  Schedule,
  AccessTime,
  Checkroom,
  Shower,
  Smartphone
} from '@mui/icons-material';
interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  icon: React.ReactNode;
}

const faqData: FAQItem[] = [
  {
    id: 1,
    question: 'Are all the classes hot?',
    answer: 'All of the classes inside our Yoga/Barre studio are HOT. In the yoga and barre studios the temperatures will range from 95 – 105 degrees. Humidity will run anywhere from 30 – 60%. The more people the hotter the temperature and higher humidity!',
    category: 'Class Experience',
    icon: <LocalFireDepartment />
  },
  {
    id: 2,
    question: 'Are the classes beginner friendly?',
    answer: 'Yes, all our classes are multi-level. Depending on the instructor style and class type, some classes will be more intense than others. If you have any physical limitations, please let your instructor know.',
    category: 'Class Experience',
    icon: <FitnessCenter />
  },
  {
    id: 3,
    question: 'How soon before class should I arrive?',
    answer: 'Arrive at the studio at least 15 minutes prior to the start of class. If you are new, please arrive 20 minutes prior to meet the instructor and get oriented to the studio. The doors to the studio will lock 5 minutes prior to the start of class, and we will not be able to accommodate late arrivals. Some classes fill up quickly; make sure you register early to get a spot.',
    category: 'Studio Policies',
    icon: <Schedule />
  },
  {
    id: 4,
    question: 'What hours are you open?',
    answer: 'Currently we will have office hours available 15 minutes prior to class as well as Saturday 8:00am -10:00am and Sunday 8:30am-10:30am. Rose and Kristi are also available by appointment to meet clients at the studio for tour and class description outside of these hours.',
    category: 'Studio Policies',
    icon: <AccessTime />
  },
  {
    id: 5,
    question: 'What do I wear/bring to class?',
    answer: 'Wear what you usually would for any yoga or exercise class: capris or tights (short or long); cami-tank or sport bra. Men can wear shorts or tights. Shirts are optional in class. We will use a mat in all classes including barre. Bring a yoga mat, sweat towel, and water bottle.',
    category: 'What to Bring',
    icon: <Checkroom />
  },
  {
    id: 6,
    question: 'Do you have showers?',
    answer: 'Yes, we have two shower rooms located in the studio. One in the men\'s room and one in the women\'s room.',
    category: 'Facilities',
    icon: <Shower />
  },
  {
    id: 7,
    question: 'Do I need to sign up beforehand?',
    answer: 'Yes! Download and create an account on our branded app – The Burn, Fit Degree. You may sign up for classes as far out as your current membership or month expires. Occasionally we can take drop-ins, but we highly encourage you to sign up online beforehand to guarantee a spot in class. If you are unable to make your scheduled class, please sign in to your account and CANCEL your reservation up to 2 hours prior to class or your account will be charged $10 for that class. No shows are also a $10 charge. Please download and create your account on our The Burn app on your phone to make signing up for classes that much easier and faster!',
    category: 'Booking & App',
    icon: <Smartphone />
  }
];

const FAQ: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Class Experience': '#ff6b6b',
      'Studio Policies': '#4ecdc4',
      'What to Bring': '#45b7d1',
      'Facilities': '#96ceb4',
      'Booking & App': '#feca57'
    };
    return colors[category] || theme.palette.primary.main;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative', overflow: 'hidden', py: 4 }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header Section */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
              <Help sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                component="h1"
                sx={{ fontWeight: 700, color: 'primary.main' }}
              >
                Frequently Asked Questions
              </Typography>
            </Stack>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Get answers to the most common questions about The Burn. Can't find what you're looking for? 
              Contact us directly!
            </Typography>
          </Box>
        </Fade>

        {/* FAQ Categories Overview */}
        <Grow in timeout={1200}>
          <Card
            elevation={4}
            sx={{
              mb: 6,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Quick Categories
              </Typography>
              <Grid container spacing={2}>
                {Array.from(new Set(faqData.map(item => item.category))).map((category) => (
                  <Grid item key={category}>
                    <Chip
                      label={category}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.3)',
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grow>

        {/* FAQ Accordion Section */}
        <Grid container spacing={3}>
          {faqData.map((item, index) => (
            <Grid item xs={12} key={item.id}>
              <Grow in timeout={1000 + (index * 100)}>
                <Card
                  elevation={2}
                  sx={{
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Accordion
                    expanded={expanded === `panel${item.id}`}
                    onChange={handleChange(`panel${item.id}`)}
                    sx={{
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls={`panel${item.id}-content`}
                      id={`panel${item.id}-header`}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.02)',
                        },
                        '&.Mui-expanded': {
                          minHeight: 64,
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                        <Box
                          sx={{
                            color: getCategoryColor(item.category),
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              textAlign: 'left',
                            }}
                          >
                            {item.question}
                          </Typography>
                          <Chip
                            label={item.category}
                            size="small"
                            sx={{
                              bgcolor: getCategoryColor(item.category),
                              color: 'white',
                              fontSize: '0.75rem',
                              height: 20,
                              mt: 0.5,
                            }}
                          />
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.7,
                          color: 'text.secondary',
                          fontSize: '1rem',
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>

        {/* Contact Section */}
        <Fade in timeout={1500}>
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Card
              elevation={3}
              sx={{
                bgcolor: 'grey.50',
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Still Have Questions?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  We're here to help! Contact Rose and Kristi directly for personalized assistance.
                </Typography>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={2}
                  sx={{ justifyContent: 'center' }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    📧 Email: rose@theburnstudio.com
                    📧 Email: kristi@theburnstudio.com
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    📱 Phone: (515) 216-3888
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default FAQ;
